import {
  Box, Container, Typography, Button, Grid,
  Card, CardContent, Stack, Chip, Divider, alpha,
} from '@mui/material'
import {
  IconClipboardList, IconMail, IconReportAnalytics,
  IconArrowRight, IconCalendarEvent, IconEyeCheck,
  IconBuildingBank, IconDatabase,
} from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { tokens } from '@/theme'
import NewsletterSignup from '@/components/NewsletterSignup'
import { trackFunnel } from '@/utils/analytics'
import { usePageMeta } from '@/utils/pageMeta'

// ─── Copy guardrail (ag-010) ──────────────────────────────────────────────────
// Every claim on this page traces to marketing-feed.json safeClaims /
// latestSignals (agrolense-group/marketing-feed.json). Do NOT add outcome
// guarantees, automation claims, or real-time claims (its doNotClaim list).

// Positioning statement — plan A3 (docs/GSA-GROWTH-ROADMAP-2026-07.md).
const POSITIONING =
  'AgroLense helps California GSAs and irrigation districts handle SGMA reporting and communication.'

const PAGE_META = {
  title: 'For GSAs — AgroLense',
  description: POSITIONING,
  url: 'https://agrolense.com/for-gsas',
}

// Discovery-call CTA. Interim per ag-010: newsletter primary + discovery-call
// mailto — replace with the owner's final CTA when ag-006 decides it.
const DISCOVERY_CALL_HREF =
  'mailto:contact@agrolense.com?subject=AgroLense%20discovery%20call'

// Demo link to the ag-001 per-GSA pilot page (/g/<slug>). Stays null until the
// owner picks the pilot GSA and its page is live; the section then swaps its
// fallback link (the GSA data explorer) for the branded demo automatically.
const PILOT_DEMO_PATH: string | null = null

// What we do for GSAs — grounded in safeClaims ("summarizes meetings and
// regulatory updates into digests") and shipped signals (human-approved
// summaries, GSA-scoped newsletter signup).
const SERVICES = [
  {
    icon: <IconClipboardList size={26} stroke={1.5} />,
    title: 'Meeting Summaries',
    body: 'Board agendas and minutes summarized into plain-language digests your stakeholders can read in minutes. A person reviews every summary before it publishes — nothing goes out automatically.',
  },
  {
    icon: <IconMail size={26} stroke={1.5} />,
    title: 'Stakeholder Newsletters',
    body: 'Curated groundwater and GSA updates delivered to the people in your basin. Subscribers pick the GSAs they care about, so your news reaches the stakeholders it concerns.',
  },
  {
    icon: <IconReportAnalytics size={26} stroke={1.5} />,
    title: 'Annual-Report Data Support',
    body: 'The decisions, regulatory updates, and meeting records from your year, organized into digests that support your annual-report and stakeholder-communication workload.',
  },
]

// Trust strip — each fact traces to a shipped signal in marketing-feed.json.
const FACTS = [
  {
    icon: <IconBuildingBank size={22} stroke={1.5} />,
    title: 'All 509 California GSAs',
    body: 'Our registry covers every California GSA, with basin priority and critical-overdraft flags.',
  },
  {
    icon: <IconEyeCheck size={22} stroke={1.5} />,
    title: 'Human review, always',
    body: 'Summaries and newsletters are drafted, then reviewed and approved by a person before anything publishes.',
  },
  {
    icon: <IconDatabase size={22} stroke={1.5} />,
    title: 'Data-grounded',
    body: 'Agriculture and governance intelligence built from IoT and geospatial data alongside your public records.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ForGsas() {
  usePageMeta(PAGE_META)

  return (
    <>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${tokens.blue.dark} 0%, ${tokens.green.dark} 100%)`,
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 7, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `radial-gradient(${alpha('#fff', 0.04)} 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Chip
            label="For GSAs & Irrigation Districts"
            size="small"
            sx={{
              fontFamily: '"Poppins", system-ui, sans-serif',
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#fff',
              background: alpha('#fff', 0.12),
              mb: 3,
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: '#fff',
              fontSize: { xs: '1.9rem', md: '2.7rem' },
              lineHeight: 1.25,
              mb: 2.5,
            }}
          >
            {POSITIONING}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 620, mx: 'auto', mb: 4 }}>
            Meeting summaries, stakeholder newsletters, and annual-report data
            support — drafted from your public records and reviewed by a person
            before anything publishes.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              href="#signup"
              endIcon={<IconArrowRight size={18} />}
            >
              Get the Newsletter
            </Button>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href={DISCOVERY_CALL_HREF}
              onClick={() =>
                trackFunnel('signup', { surface: 'for_gsas_page', step: 'discovery_call_click' })
              }
              startIcon={<IconCalendarEvent size={18} />}
              sx={{
                borderColor: '#fff !important',
                borderWidth: '2px !important',
                color: '#fff !important',
                '&:hover': { background: alpha('#fff', 0.1) },
              }}
            >
              Book a Discovery Call
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What we do for GSAs */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }} id="what-we-do">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
            What We Do for Your GSA
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            The Communication Side of SGMA, Off Your Plate
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {SERVICES.map(({ icon, title, body }) => (
            <Grid key={title} size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex', p: 1.25, borderRadius: 1.5,
                      background: alpha(tokens.green.main, 0.08),
                      color: tokens.green.main, mb: 2,
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{body}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider sx={{ mx: { xs: 2, md: 8 } }} />

      {/* Trust strip */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={2.5}>
          {FACTS.map(({ icon, title, body }) => (
            <Grid key={title} size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    display: 'inline-flex', p: 1, borderRadius: 1.5,
                    background: alpha(tokens.blue.main, 0.08),
                    color: tokens.blue.main, flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {body}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Demo */}
      <Box sx={{ background: tokens.green.dark, color: '#fff', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="overline" sx={{ color: alpha('#fff', 0.45), display: 'block', mb: 1 }}>
            See It in Action
          </Typography>
          <Typography variant="h2" sx={{ color: '#fff', mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            {PILOT_DEMO_PATH
              ? 'A Branded Intelligence Page for a Real GSA'
              : 'Explore the GSA Data We Already Track'}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 560, mx: 'auto', mb: 4 }}>
            {PILOT_DEMO_PATH
              ? 'Meetings with human-approved summaries, published news, and a GSA-scoped newsletter signup — on a page carrying the GSA’s own branding.'
              : 'Browse basin priority, critical-overdraft status, and contact data for every California GSA in our registry.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            to={PILOT_DEMO_PATH ?? '/gsa-data'}
            endIcon={<IconArrowRight size={18} />}
          >
            {PILOT_DEMO_PATH ? 'View the Pilot Page' : 'Browse GSA Data'}
          </Button>
        </Container>
      </Box>

      {/* Newsletter CTA (T-202 lead path via the shared signup component) */}
      <Box id="signup">
        <NewsletterSignup />
      </Box>
    </>
  )
}
